import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Stack, Box, Inline, Text } from '@atlaskit/primitives';
import Image from '@atlaskit/image';
import { cssMap } from '@atlaskit/css';
import Button from '@atlaskit/button/new';

import { ErrorDisplay } from '@confluence/error-boundary';

import AudioPanelErrorIllustration from '../../assets/AudioPanelErrorIllustration.svg';
import AudioPanelErrorIllustrationDark from '../../assets/AudioPanelErrorIllustrationDark.svg';
import { useAudioBriefingActions } from '../../hooks/useAudioBriefing';

import { AudioBriefingPopupControlButton } from './AudioBriefingPopupControlButton';

const styles = cssMap({
	wrapper: {
		width: '300px',
	},
	errorIllustrationContainer: {
		width: '100px',
		height: '100px',
	},
	errorTextContainer: {
		textAlign: 'center',
		whiteSpace: 'pre-line',
	},
});

const i18n = defineMessages({
	errorMessage: {
		id: 'audio.audio-briefing-popup.error.message',
		defaultMessage: 'We couldn’t load the audio for this briefing.',
		description: 'Error message for the audio briefings popup',
	},
	errorImgAlt: {
		id: 'audio.audio-briefing-popup.error.img.alt',
		defaultMessage: 'Audio briefings popup error illustration',
		description: 'Alt text for the audio briefings popup error illustration',
	},
	tryAgainButtonLabel: {
		id: 'audio.audio-briefing-popup.error.try-again',
		defaultMessage: 'Try again',
		description: 'Text for button that allows users to try loading the briefing again',
	},
});

type AudioBriefingsErrorComponentProps = {
	error: Error;
	audioRef: React.RefObject<HTMLAudioElement>;
	closePopup: () => void;
};

export const AudioBriefingErrorComponent: FC<AudioBriefingsErrorComponentProps> = ({
	error,
	audioRef,
	closePopup,
}) => {
	const { formatMessage } = useIntl();
	const { fetchBriefing } = useAudioBriefingActions();

	const handleRetry = async () => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
		fetchBriefing();
	};

	return (
		<Box xcss={styles.wrapper} padding="space.200">
			<Inline alignInline="end">
				<AudioBriefingPopupControlButton closePopup={closePopup} />
			</Inline>
			<Stack alignInline="center" space="space.100">
				<Inline xcss={styles.errorIllustrationContainer} alignInline="center" alignBlock="center">
					<Image
						src={AudioPanelErrorIllustration}
						alt={formatMessage(i18n.errorImgAlt)}
						srcDark={AudioPanelErrorIllustrationDark}
					/>
				</Inline>

				<Box xcss={styles.errorTextContainer}>
					<Text size="large" weight="bold">
						{formatMessage(i18n.errorMessage)}
					</Text>
				</Box>

				<Box paddingBlock="space.050">
					<Button onClick={handleRetry}>{formatMessage(i18n.tryAgainButtonLabel)}</Button>
				</Box>

				<ErrorDisplay error={error} />
			</Stack>
		</Box>
	);
};
